import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from '@heroicons/react/outline';
import clsx from 'clsx';
import { Fragment, ReactNode, useEffect } from 'react';
import { setIsIntercomVisible } from '../../../adapters/intercomAdapter';
import { TW_RING } from '../../../domain/websiteDomain';

interface SideModalProps {
  title: string;
  footer: ReactNode;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const SideModal: React.FC<SideModalProps> = ({
  children,
  title,
  footer,
  isOpen,
  setIsOpen,
}) => {
  useEffect(() => {
    setIsIntercomVisible(!isOpen);
  }, [isOpen]);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 z-50 overflow-hidden"
        open={isOpen}
        onClose={setIsOpen}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 transition-opacity bg-black bg-opacity-75" />
          </Transition.Child>

          <div className="fixed inset-y-0 right-0 flex max-w-full pl-10">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="w-screen max-w-sm">
                <div className="flex flex-col h-screen bg-white shadow-xl">
                  <div className="border-b px-16px py-24px border-ice">
                    <div className="flex items-start justify-between">
                      <Dialog.Title className="text-xl text-black font-family-bold">
                        {title}
                      </Dialog.Title>
                      <div className="flex items-center ml-3 h-7">
                        <button
                          className={clsx(
                            'bg-white rounded-md text-gray-400 hover:text-gray-500',
                            TW_RING
                          )}
                          onClick={() => setIsOpen(false)}
                        >
                          <span className="sr-only">Close panel</span>
                          <XIcon className="w-6 h-6" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="relative z-0 flex-1 flex-grow overflow-y-auto">
                    {children}
                  </div>
                  <div className="z-10 shadow-linear">{footer}</div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default SideModal;
